// frontend/src/hooks/useToast.js
// Shared toast notification context — every component that calls useToast() now
// reads/writes the SAME queue, instead of each getting its own isolated state.

import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now();
    const toast = { id, message, type };

    setToasts(prev => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const success = useCallback((message) => addToast(message, 'success', 3000), [addToast]);
  const error = useCallback((message) => addToast(message, 'error', 4000), [addToast]);
  const info = useCallback((message) => addToast(message, 'info', 3000), [addToast]);
  const warning = useCallback((message) => addToast(message, 'warning', 3000), [addToast]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const value = { toasts, addToast, success, error, info, warning, removeToast };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default useToast;